import { useCallback, useEffect, useRef, useState } from "react";
import { Api } from "../../lib/api";
import { WidgetConfigStore } from "../../lib/cache";
import { Button, StatusDot, UsageBar } from "../../components/ui";
import "./widget.css";

export default function Widget() {
  const [cards, setCards] = useState([]);
  const [visible, setVisible] = useState(new Set());
  const [order, setOrder] = useState([]);
  const [totalAllowance, setTotalAllowance] = useState(0);
  const [phase, setPhase] = useState("loading"); // loading | content | error
  const [isDirty, setIsDirty] = useState(false);
  const [saveResult, setSaveResult] = useState(null); // { type, message }
  const [filterOpen, setFilterOpen] = useState(false);

  const lastSaved = useRef({ visible: new Set(), order: [] });

  const refresh = useCallback(async () => {
    setPhase("loading");
    try {
      const cached = WidgetConfigStore.get();
      const [monitor, widgetCfg] = await Promise.all([Api.getMonitor(), Api.getWidgetConfig().catch(() => cached)]);

      const cardList = monitor?.llm_cards ?? [];
      const allowance = (monitor?.tokens?.tokens_available ?? 0) + (monitor?.tokens?.tokens_used ?? 0);

      const resolvedVisible = resolveVisible(cardList, widgetCfg?.visible_llms ?? []);
      const resolvedOrder = resolveOrder(cardList, resolvedVisible, widgetCfg?.llm_order ?? []);

      lastSaved.current = { visible: resolvedVisible, order: resolvedOrder };
      setCards(cardList);
      setVisible(resolvedVisible);
      setOrder(resolvedOrder);
      setTotalAllowance(allowance);
      setPhase("content");
      setIsDirty(false);
    } catch {
      setPhase("error");
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  function getCardLabel(card) {
    const group = cards.filter((c) => c.llm === card.llm);
    if (group.length <= 1) return capitalize(card.llm);
    const index = group.findIndex((c) => c.id === card.id);
    return `${capitalize(card.llm)} #${index + 1}`;
  }

  function toggleVisible(cardId, checked) {
    setVisible((prevSet) => {
      const next = new Set(prevSet);
      if (checked) next.add(cardId);
      else next.delete(cardId);

      setOrder((prevOrder) => {
        const newOrder =
          checked && !prevOrder.includes(cardId) ? [...prevOrder, cardId] : prevOrder.filter((id) => next.has(id));
        checkDirty(next, newOrder);
        return newOrder;
      });
      return next;
    });
  }

  function checkDirty(newVisible, newOrder) {
    const same =
      setsEqual(newVisible, lastSaved.current.visible) && arraysEqual(newOrder, lastSaved.current.order);
    setIsDirty(!same);
  }

  function reorder(fromId, toId) {
    setOrder((prev) => {
      const from = prev.indexOf(fromId);
      const to = prev.indexOf(toId);
      if (from === -1 || to === -1 || from === to) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      checkDirty(visible, next);
      return next;
    });
  }

  async function save() {
    setSaveResult(null);
    try {
      const cfg = (await Api.getWidgetConfig().catch(() => null)) || { user_id: null, visible_llms: [], llm_order: [], enabled: true };
      const updated = { ...cfg, visible_llms: Array.from(visible), llm_order: order };
      WidgetConfigStore.save(updated);
      await Api.setWidgetConfig(updated);
      lastSaved.current = { visible: new Set(visible), order };
      setIsDirty(false);
      setSaveResult({ type: "success", message: "Settings saved" });
      setTimeout(() => setSaveResult(null), 1600);
    } catch (e) {
      setSaveResult({ type: "error", message: e.message || "Couldn't save settings" });
    }
  }

  const orderedVisibleCards = order.map((id) => cards.find((c) => c.id === id)).filter(Boolean);

  return (
    <div className="tg-widget-screen">
      <div className="tg-widget-scroll tg-scroll">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h1 style={{ fontSize: 24 }}>Widget preview</h1>
          <div style={{ position: "relative" }}>
            <button className="tg-icon-btn" onClick={() => setFilterOpen((v) => !v)} aria-label="Filter">
              <FilterIcon />
            </button>
            {filterOpen && (
              <>
                <div className="tg-menu-backdrop" onClick={() => setFilterOpen(false)} />
                <div className="tg-widget-filter">
                  {cards.length === 0 ? (
                    <p style={{ padding: "10px 14px", fontSize: 12.5, color: "var(--muted-slate)" }}>
                      No LLMs connected
                    </p>
                  ) : (
                    cards.map((card) => (
                      <label key={card.id} className="tg-widget-filter__item">
                        <input
                          type="checkbox"
                          checked={visible.has(card.id)}
                          onChange={(e) => toggleVisible(card.id, e.target.checked)}
                        />
                        <span>{getCardLabel(card)}</span>
                      </label>
                    ))
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <p style={{ fontSize: 12.5, color: "var(--slate)", marginTop: 4, marginBottom: 16 }}>
          This is how the bars will appear on your widget. Drag a bar by its handle to reorder.
        </p>

        {phase === "loading" && <LoadingState />}
        {phase === "error" && <ErrorState onRetry={refresh} />}
        {phase === "content" && (
          <DraggablePreview
            orderedVisibleCards={orderedVisibleCards}
            totalAllowance={totalAllowance}
            getCardLabel={getCardLabel}
            onReorder={reorder}
          />
        )}

        <div style={{ height: 90 }} />
      </div>

      {phase === "content" && (
        <div className="tg-widget-savebar">
          {saveResult && (
            <div className={`tg-toast ${saveResult.type === "success" ? "tg-toast--success" : "tg-toast--error"}`} style={{ marginBottom: 10 }}>
              {saveResult.message}
            </div>
          )}
          <Button fullWidth disabled={!isDirty} onClick={save}>
            {isDirty ? "Save settings" : "Saved"}
          </Button>
        </div>
      )}
    </div>
  );
}

function resolveVisible(cards, configVisible) {
  const ids = new Set();
  configVisible.forEach((entry) => {
    const byId = cards.find((c) => c.id === entry);
    if (byId) ids.add(byId.id);
    else cards.filter((c) => c.llm === entry).forEach((c) => ids.add(c.id));
  });
  if (ids.size === 0 && configVisible.length === 0) cards.forEach((c) => ids.add(c.id));
  return ids;
}

function resolveOrder(cards, visible, configOrder) {
  const ids = [];
  configOrder.forEach((entry) => {
    const byId = cards.find((c) => c.id === entry);
    if (byId) {
      if (!ids.includes(byId.id)) ids.push(byId.id);
    } else {
      cards.filter((c) => c.llm === entry).forEach((c) => {
        if (!ids.includes(c.id)) ids.push(c.id);
      });
    }
  });
  const missing = Array.from(visible).filter((id) => !ids.includes(id));
  return ids.filter((id) => visible.has(id)).concat(missing);
}

function setsEqual(a, b) {
  if (a.size !== b.size) return false;
  for (const v of a) if (!b.has(v)) return false;
  return true;
}
function arraysEqual(a, b) {
  if (a.length !== b.length) return false;
  return a.every((v, i) => v === b[i]);
}
function capitalize(s) {
  return s ? s[0].toUpperCase() + s.slice(1) : s;
}

function DraggablePreview({ orderedVisibleCards, totalAllowance, getCardLabel, onReorder }) {
  const [draggingId, setDraggingId] = useState(null);
  const [overId, setOverId] = useState(null);

  if (orderedVisibleCards.length === 0) {
    return (
      <div className="tg-card" style={{ textAlign: "center", color: "var(--slate)", padding: 32 }}>
        No bars selected
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {orderedVisibleCards.map((card) => {
        const percent = totalAllowance > 0 ? (card.tokens_used / totalAllowance) * 100 : 0;
        const isDragging = draggingId === card.id;
        const isOver = overId === card.id && draggingId && draggingId !== card.id;
        return (
          <div
            key={card.id}
            className={`tg-widget-bar ${isDragging ? "tg-widget-bar--dragging" : ""} ${isOver ? "tg-widget-bar--over" : ""}`}
            draggable
            onDragStart={() => setDraggingId(card.id)}
            onDragEnd={() => {
              setDraggingId(null);
              setOverId(null);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              setOverId(card.id);
            }}
            onDrop={(e) => {
              e.preventDefault();
              if (draggingId && draggingId !== card.id) onReorder(draggingId, card.id);
              setDraggingId(null);
              setOverId(null);
            }}
          >
            <span className="tg-widget-bar__handle" title="Drag to reorder">
              <GripIcon />
            </span>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <StatusDot status={card.status} />
                <span style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>{getCardLabel(card)}</span>
                <span style={{ fontSize: 13, fontWeight: 700 }}>{percent.toFixed(1)}%</span>
              </div>
              <div style={{ height: 4 }} />
              <UsageBar percent={percent} danger={percent >= 80} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function LoadingState() {
  return (
    <div style={{ display: "flex", justifyContent: "center", padding: "60px 0" }}>
      <span className="tg-spinner" style={{ width: 26, height: 26, borderWidth: 3 }} />
    </div>
  );
}
function ErrorState({ onRetry }) {
  return (
    <div style={{ textAlign: "center", padding: "40px 0" }}>
      <p style={{ fontSize: 13.5, color: "var(--error)" }}>Couldn&apos;t load settings</p>
      <button className="tg-btn tg-btn--text tg-btn--primary" onClick={onRetry}>
        Retry
      </button>
    </div>
  );
}

function FilterIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 4h16l-6 8v6l-4 2v-8L4 4z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function GripIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
      <circle cx="9" cy="6" r="1.6" />
      <circle cx="15" cy="6" r="1.6" />
      <circle cx="9" cy="12" r="1.6" />
      <circle cx="15" cy="12" r="1.6" />
      <circle cx="9" cy="18" r="1.6" />
      <circle cx="15" cy="18" r="1.6" />
    </svg>
  );
}
